import { DonutChart, Footnote, ProgressBar, SectionCard, type DonutSlice } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatDate, formatRatio } from '../../../lib/format';
import { porUnidade, razaoBps } from '../comparar';
import type { EmailCampaign, EmailCampaignStatus, EmailTotals, PeriodWindow } from '../types';
import { EMAIL_STATUS_LABEL } from '../vocabulario';
import { GradeDeRetorno, type MedidaDeRetorno } from './GradeDeRetorno';

/**
 * Retorno das campanhas de e-mail no periodo, o caminho do aceito ao pago e
 * o anel por status.
 *
 * Decisoes nao obvias:
 *  1. Todos os numeros saem da MESMA lista de campanhas (ja com os ajustes
 *     locais): faixa, caminho e anel batem entre si por construcao.
 *  2. Comparacao com o periodo anterior em cada medida (glifo + texto).
 *     Taxas (entrega, descadastro) comparam em pontos ("de 2,1% para 1,8%"),
 *     nunca em variacao relativa de percentual.
 *  3. Descadastro e "menor e melhor"; custo e neutro (sobe com o volume).
 *  4. "Pagaram" e medido sobre os ACEITOS, nao sobre quem clicou: pelo
 *     ADR-001 o pagamento e creditado ao ultimo contato mesmo sem clique
 *     (a pessoa pode ir direto ao checkout). Sobre cliques, a taxa passaria
 *     de 100% com dado real. Mesma base do WhatsApp ("dos entregues").
 */

const ORDEM_STATUS: readonly EmailCampaignStatus[] = ['rascunho', 'agendada', 'enviando', 'concluida'];
const TOM_DO_STATUS: Readonly<Record<EmailCampaignStatus, DonutSlice['tone']>> = { rascunho: 1, agendada: 2, enviando: 3, concluida: 4 };

export function KpisEmail({
  campanhas,
  anterior,
  periodoAnterior,
  porMilCents,
  periodoTexto,
}: {
  readonly campanhas: readonly EmailCampaign[];
  readonly anterior: EmailTotals | null;
  readonly periodoAnterior: PeriodWindow | null;
  readonly porMilCents: number;
  readonly periodoTexto: string;
}): JSX.Element {
  const soma = (fn: (c: EmailCampaign) => number): number => campanhas.reduce((acc, c) => acc + fn(c), 0);
  const aceitos = soma((c) => c.acceptedCount);
  const falhas = soma((c) => c.failedCount);
  const abertos = soma((c) => c.openedCount);
  const cliques = soma((c) => c.clickedCount);
  const descadastros = soma((c) => c.unsubscribedCount);
  const pagaram = soma((c) => c.paidCount);
  const recuperado = soma((c) => c.recoveredAmountCents);
  const comissao = soma((c) => c.commissionCents);
  const custo = Math.round((aceitos * porMilCents) / 1_000);
  const custoAnterior = anterior === null ? null : Math.round((anterior.accepted * porMilCents) / 1_000);
  const unitario = porUnidade(custo, pagaram);

  const entregaBps = razaoBps(aceitos, aceitos + falhas);
  const entregaAnteriorBps = anterior === null ? null : razaoBps(anterior.accepted, anterior.accepted + anterior.failed);
  const descadastroBps = razaoBps(descadastros, aceitos);
  const descadastroAnteriorBps = anterior === null ? null : razaoBps(anterior.unsubscribed, anterior.accepted);

  const medidas: readonly MedidaDeRetorno[] = [
    {
      chave: 'recuperado',
      rotulo: 'Recuperado pelos e-mails',
      valor: formatBRLFromCents(recuperado),
      atual: recuperado,
      anterior: anterior === null ? null : anterior.recoveredAmountCents,
      tipo: 'dinheiro',
      apoio: 'Pagamentos em até 7 dias com o e-mail como último contato.',
      icone: 'moeda',
      destaque: true,
    },
    {
      chave: 'comissao',
      rotulo: 'Comissão',
      valor: formatBRLFromCents(comissao),
      atual: comissao,
      anterior: anterior === null ? null : anterior.commissionCents,
      tipo: 'dinheiro',
      apoio: 'O que o Converte Fácil fatura sobre o recuperado.',
      icone: 'comissao',
    },
    {
      chave: 'pagaram',
      rotulo: 'Pagaram',
      valor: formatCount(pagaram),
      atual: pagaram,
      anterior: anterior === null ? null : anterior.paid,
      tipo: 'contagem',
      apoio: `${formatRatio(pagaram, aceitos)} dos e-mails aceitos, com o e-mail como último contato.`,
      icone: 'check',
    },
    {
      chave: 'aceitos',
      rotulo: 'E-mails enviados',
      valor: formatCount(aceitos),
      atual: aceitos,
      anterior: anterior === null ? null : anterior.accepted,
      tipo: 'contagem',
      apoio: `Aceitos pelo provedor; ${formatCount(falhas)} recusados.`,
      icone: 'disparo',
    },
    {
      chave: 'entrega',
      rotulo: 'Taxa de entrega',
      valor: formatRatio(aceitos, aceitos + falhas),
      atual: entregaBps ?? 0,
      anterior: entregaAnteriorBps,
      tipo: 'bps',
      apoio: 'Aceitos sobre o total tentado. Abaixo de 95%, revise a lista.',
      icone: 'escudo',
      semComparacao: entregaBps === null ? 'nenhum e-mail tentado no período' : undefined,
    },
    {
      chave: 'descadastro',
      rotulo: 'Descadastros',
      valor: formatCount(descadastros),
      atual: descadastroBps ?? 0,
      anterior: descadastroAnteriorBps,
      tipo: 'bps',
      sentido: 'menor_melhor',
      apoio: `${formatRatio(descadastros, aceitos)} dos enviados. Nunca mais entram em campanha.`,
      icone: 'saida',
      semComparacao: descadastroBps === null ? 'nenhum e-mail enviado no período' : undefined,
    },
  ];

  const fatias: readonly DonutSlice[] = ORDEM_STATUS.map((status) => {
    const total = campanhas.filter((c) => c.status === status).length;
    return { key: status, label: EMAIL_STATUS_LABEL[status], value: total, tone: TOM_DO_STATUS[status], display: formatCount(total) };
  }).filter((f) => f.value > 0);

  const caminho: readonly { readonly rotulo: string; readonly valor: number; readonly base: number; readonly baseRotulo: string }[] = [
    { rotulo: 'Aceitos', valor: aceitos, base: aceitos + falhas, baseRotulo: 'dos tentados' },
    { rotulo: 'Abriram', valor: abertos, base: aceitos, baseRotulo: 'dos aceitos' },
    { rotulo: 'Clicaram no botão', valor: cliques, base: abertos, baseRotulo: 'dos que abriram' },
    { rotulo: 'Pagaram em até 7 dias', valor: pagaram, base: aceitos, baseRotulo: 'dos aceitos' },
  ];

  return (
    <div className="flex flex-col gap-6">
      <GradeDeRetorno
        id="retorno-email"
        titulo="Retorno do e-mail"
        descricao={`Todas as empresas · ${periodoTexto}, com a variação contra o período anterior de mesmo tamanho.`}
        medidas={medidas}
        rodape={
          <Footnote>
            Custo estimado de {formatBRLFromCents(custo)} ({formatBRLFromCents(porMilCents)} por mil aceitos)
            {custoAnterior === null ? '' : `, contra ${formatBRLFromCents(custoAnterior)} no período anterior`}
            {unitario === null ? '' : ` · ${formatBRLFromCents(unitario)} por recuperação`}.{' '}
            {periodoAnterior === null ? 'A janela "desde o início" não tem período anterior.' : `Comparado com ${formatDate(periodoAnterior.start)} a ${formatDate(periodoAnterior.end)}.`}
          </Footnote>
        }
      />
      <div className="grid gap-6 lg:grid-cols-3">
        <SectionCard id="caminho-email" icon="funil" title="Do aceito ao pago" description="Abertura e clique sobre a etapa anterior; pagamento sobre os aceitos (quem paga sem clicar também conta, se o e-mail foi o último contato). Abertura depende do cliente de e-mail carregar imagens." className="lg:col-span-2">
          <ol className="flex flex-col gap-3">
            {caminho.map((etapa) => (
              <li key={etapa.rotulo} className="flex flex-col gap-1">
                <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                  <span className="text-ink">
                    <span className="font-semibold tabular-nums">{formatCount(etapa.valor)}</span> {etapa.rotulo.toLowerCase()}
                  </span>
                  <span className="text-xs tabular-nums text-ink-subtle">
                    {formatRatio(etapa.valor, etapa.base)} {etapa.baseRotulo}
                  </span>
                </div>
                <ProgressBar value={etapa.valor} max={Math.max(1, aceitos + falhas)} tone="accent" label={`${etapa.rotulo}: ${formatCount(etapa.valor)}`} />
              </li>
            ))}
          </ol>
        </SectionCard>
        <SectionCard id="campanhas-por-status" title="Campanhas por status" description="Quantas estão em cada etapa, no período.">
          {fatias.length === 0 ? (
            <p className="text-sm text-ink-muted">Nenhuma campanha no período. Escreva a primeira em "Nova campanha".</p>
          ) : (
            <DonutChart slices={fatias} title="Campanhas por status" center={{ value: formatCount(campanhas.length), label: 'campanhas' }} />
          )}
        </SectionCard>
      </div>
    </div>
  );
}
